import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { ValidationApiError } from '@/shared/api/ValidationApiError';

type UpdateOrganizationRequest = components['schemas']['UpdateOrganizationEndpoint_UpdateOrganizationRequest'];
type UpdateOrganizationResponse = components['schemas']['UpdateOrganizationEndpoint_UpdateOrganizationResponse'];

export function useUpdateOrganization() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (request: UpdateOrganizationRequest): Promise<UpdateOrganizationResponse> => {
      const client = createApiClient(getAccessToken);
      const { data } = await client.PUT(`/api/organizations/${request.id!}` as unknown as '/api/organizations/{organizationId}', {
        body: request,
      });

      if (!data) {
        throw new Error('No data returned from API');
      }

      return data as UpdateOrganizationResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      if (data.id) {
        queryClient.invalidateQueries({ queryKey: [data.id] });
      }
    },
  });

  return {
    updateOrganization: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    validationErrors: mutation.error instanceof ValidationApiError ? mutation.error.errors : null,
  };
}
